import Layout from "@/components/feature/Layout";
import PageHero from "@/components/base/PageHero";
import SectionHeader from "@/components/base/SectionHeader";
import CTAPanel from "@/components/base/CTAPanel";

const strongOpportunities = [
  {
    title: "Web Application Development & Modernization",
    description: "C#, ASP.NET / .NET, SQL Server, HTML/CSS/JavaScript application analysis, development, testing, and support. Legacy application enhancement, migration, maintenance, and production support. Database-backed portals, workflow applications, reporting tools, and administrative systems.",
    tags: ["Strong", "C#", ".NET", "SQL Server"],
  },
  {
    title: "SharePoint, Intranet & Collaboration Portals",
    description: "SharePoint administration and development, templates, master pages, permissions, content organization, and migration. Power Apps, Power Automate solutions, integration, implementation, and user training. Enterprise document libraries, internal search, and collaboration platforms.",
    tags: ["Strong", "SharePoint", "Power Platform"],
  },
  {
    title: "Government Application Support & Enhancement",
    description: "Long-duration public-sector delivery, enhancements, administration, reporting, technical deliverables, and operational support. Application operations, enhancements, and modernization for federal, state, or local agencies with SOW-defined responsibilities and deliverables.",
    tags: ["Strong", "Federal", "SOW Delivery"],
  },
  {
    title: "Database-Backed Web Systems & Data Migration",
    description: "SQL Server, database applications, content and data migration, integration, and reporting. Database-backed portals, data migration during platform changes, and integration with enterprise data sources.",
    tags: ["Strong", "SQL Server", "Data Migration"],
  },
  {
    title: "Enterprise Application Development",
    description: "Full SDLC, .NET / MVC, Angular, JavaScript, SQL, testing, and production support. Custom or configurable .NET web applications, enterprise software development, and application lifecycle management.",
    tags: ["Strong", "Full SDLC", "Angular"],
  },
];

const conditionalOpportunities = [
  {
    title: "Website Redesign & CMS Modernization",
    description: "Web design and development capabilities exist, but a recent public-facing website, UX portfolio, and direct WordPress/CMS proof are needed for stronger bids. Pursue with a recent work sample or qualified partner.",
    tags: ["Conditional", "CMS", "UX"],
    gap: "Add a recent live website, UX designer, WordPress/CMS proof, analytics, and measurable outcomes.",
  },
  {
    title: "Content Migration & Document Libraries",
    description: "Good SharePoint and content capabilities. Strengthen with migration quantities, metadata model, redirects, and measurable outcomes. Enterprise document library organization, content migration, and information architecture.",
    tags: ["Conditional", "SharePoint", "Content Migration"],
    gap: "Quantify pages/documents, metadata, redirects, acceptance results, and migration verification.",
  },
  {
    title: "Cloud Hosting, Infrastructure & Platform Support",
    description: "Web hosting, VMware, and administration capabilities. Confirm cloud platform, security ownership, SLAs, and architecture credentials. Pursue only after confirming cloud platform credentials and security responsibility.",
    tags: ["Conditional", "VMware", "Cloud"],
    gap: "Add named cloud architects, platform certifications, SLAs, backup/restore procedures, and security responsibility.",
  },
  {
    title: "QA, Testing, Training & Post-Launch Support",
    description: "Testing, implementation, reporting, and training capabilities. Name a QA lead and provide artifacts and results. Quality assurance, user acceptance testing, deployment support, and post-launch training.",
    tags: ["Conditional", "QA", "Training"],
    gap: "Name a QA lead and provide artifacts, test results, and measurable outcomes.",
  },
];

const gapOpportunities = [
  {
    title: "Accessibility / WCAG & Document Remediation",
    description: "Do not claim a mature accessibility practice without a named specialist, audit examples, and remediated document samples. Pursue only with a qualified partner or after adding a dedicated accessibility specialist.",
    tags: ["Gap / Partner", "WCAG", "Accessibility"],
    gap: "Add an accessibility specialist, manual testing samples, audit reports, and document-remediation examples.",
  },
  {
    title: "Cybersecurity Managed Services",
    description: "Infrastructure and security tasks may be components, but avoid leading full SOC, penetration testing, incident response, or compliance programs without direct contracts and certifications. Pursue only as a partner or with specialized credentials.",
    tags: ["Gap / Partner", "Security", "SOC"],
    gap: "Add direct contracts, named security specialists, certifications, and verifiable service delivery capabilities.",
  },
];

const separateOpportunities = [
  {
    title: "IT Staff Augmentation",
    description: "Bid only when the solicitation is explicitly labor-hour staffing. Keep this capability separate from project-delivery case studies. Technical resource placement for defined roles and periods.",
    tags: ["Separate", "Staffing", "Labor-Hour"],
    gap: "Bid only when the solicitation is explicitly labor-hour staffing. Keep separate from project-delivery case studies.",
  },
];

function ProjectCard({ item }: { item: typeof strongOpportunities[0] }) {
  return (
    <div className="card-base p-6">
      <h3 className="text-lg font-bold text-foreground-900 mb-2">
        {item.title}
      </h3>
      <p className="text-sm text-foreground-600 leading-relaxed mb-3">
        {item.description}
      </p>
      {(item as any).gap && (
        <div className="mt-3 bg-accent-50 border border-accent-200 rounded-lg p-3">
          <p className="text-xs text-accent-700">
            <span className="font-semibold">Gap to close:</span> {(item as any).gap}
          </p>
        </div>
      )}
    </div>
  );
}

export default function ProjectTypes() {
  return (
    <Layout>
      <PageHero
        title="Types & Bid Categories"
        subtitle="Where We Can Compete"
        description="A practical map of where Mitaja can bid, which capabilities support each category, and what must be in place before we pursue."
        size="lg"
      />

      <section className="section-padding bg-white">
        <div className="container-page">
          <div className="max-w-3xl mx-auto mb-14">
            <div className="bg-accent-50 border border-accent-200 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <i className="ri-information-line text-accent-600 text-xl mt-0.5 shrink-0"></i>
                <div>
                  <h3 className="text-sm font-bold text-accent-800 uppercase tracking-wider mb-2">
                    How to Read This Guide
                  </h3>
                  <p className="text-sm text-accent-700 leading-relaxed mb-2">
                    Pursue when the solicitation matches the documented technologies, duties, and deliverables.
                  </p>
                  <p className="text-sm text-accent-700 leading-relaxed mb-2">
                    Pursue only with a qualified partner, a recent work sample, or additional verified capabilities.
                  </p>
                  <p className="text-sm text-accent-700 leading-relaxed">
                    Do not lead with this category until Mitaja has the named personnel, suitable references, and delivery readiness in place.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <SectionHeader
            title="Primary Opportunities"
            subtitle="Pursue when the solicitation matches documented technologies, duties, and deliverables."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-14">
            {strongOpportunities.map((item) => (
              <ProjectCard key={item.title} item={item} />
            ))}
          </div>

          <SectionHeader
            title="Opportunities Requiring Preparation"
            subtitle="Realistic, but pursue only after the listed gap is closed or covered by a qualified partner."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-14">
            {conditionalOpportunities.map((item) => (
              <ProjectCard key={item.title} item={item} />
            ))}
          </div>

          <SectionHeader
            title="Partner or Develop"
            subtitle="Do not lead with these categories until named specialists and suitable references are in place."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-14">
            {gapOpportunities.map((item) => (
              <ProjectCard key={item.title} item={item} />
            ))}
          </div>

          <SectionHeader
            title="Separate Pursuit Category"
            subtitle="Keep this capability separate from project-delivery case studies."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-14">
            {separateOpportunities.map((item) => (
              <ProjectCard key={item.title} item={item} />
            ))}
          </div>

          <div className="bg-background-100 rounded-lg p-6 border border-background-200/80">
            <h3 className="text-lg font-bold text-foreground-900 mb-3">
              Bid / No-Bid Screening Rule
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed mb-3">
              A project should normally move to bid preparation when Mitaja has:
            </p>
            <ol className="list-decimal list-inside space-y-2 text-sm text-foreground-600 mb-4">
              <li>At least one comparable verified project</li>
              <li>A contactable reference</li>
              <li>Named personnel covering mandatory roles</li>
              <li>A compliant work sample where requested</li>
              <li>A credible delivery and pricing model</li>
            </ol>
            <p className="text-sm text-foreground-600 leading-relaxed">
              If two or more of these are missing, pursue a qualified teaming partner or treat the opportunity as no-bid until the gaps are closed.
            </p>
          </div>
        </div>
      </section>

      <CTAPanel
        title="Need a Capability Verified?"
        description="Review our capabilities, case studies, and contactable references for any project type before bid preparation."
        primaryAction={{ label: "Contact Us", href: "/contact" }}
        secondaryAction={{ label: "View Capabilities", href: "/capabilities" }}
        variant="dark"
      />
    </Layout>
  );
}